import style from '../CreditOnboardingEgyptLuckyV1/CreditOnboardingEgyptLuckyV1.css';
import TextField from '../../shared/TextField/TextField';
import Button from '../../shared/Button/Button';
import useTranslation from '../../../localization/i18n.context';
import Title from '../../shared/Typography/Title';
import SelectBox from '../../shared/SelectBox/SelectBox';
import { useState, useEffect } from 'preact/hooks';

const StepField = {
  EMPLOYMENT_STATUS: 'employmentStatus',
  SECTOR_NAME: 'sectorName',
  JOB_TITLE: 'jobTitle',
  JOB_ANTIQUITY: 'jobAntiquity',
};

export default function CurrentJobStep({
  onInputChange,
  onButtonClick,
  data = {},
  employmentStatuses = [],
  jobSectors,
  stepNumber,
  stepCount,
  jobAntiquities,
}) {
  const { t } = useTranslation();
  const [jobTitles, setJobTitles] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedJobTitle, setSelectedJobTitle] = useState('');
  const [selectedJobAntiquity, setSelectedJobAntiquity] = useState('');
  const isButtonEnabled = Object.values(StepField).every(
    field => data[field] && data[field].value && data[field].value?.trim(),
  );

  useEffect(() => {
    updateSelectedValues(data);
  }, [data, employmentStatuses, jobSectors, jobAntiquities]);

  function updateSelectedValues(formData) {
    if (formData[StepField.EMPLOYMENT_STATUS]) {
      const status = employmentStatuses.find(
        s => s.id == formData[StepField.EMPLOYMENT_STATUS]?.value,
      );
      setSelectedStatus(status?.name ?? '');

      if (formData[StepField.SECTOR_NAME]) {
        const sector = jobSectors.find(
          s => s.id == formData[StepField.SECTOR_NAME]?.value,
        );
        setSelectedSector(sector?.name ?? '');
        setJobTitles(sector?.jobTitles ?? []);

        if (formData[StepField.JOB_TITLE]?.value?.length > 0) {
          const title = sector?.jobTitles?.find(
            t => t.id == formData[StepField.JOB_TITLE]?.value,
          );
          setSelectedJobTitle(title?.name);
        }
      }
    }

    if (formData[StepField.JOB_ANTIQUITY]) {
      const antiquity = jobAntiquities.find(
        a => a.id == formData[StepField.JOB_ANTIQUITY].value,
      );
      setSelectedJobAntiquity(antiquity?.name);
    }
  }

  const handleEmploymentStatusChange = employmentStatus => {
    const status = employmentStatuses.find(s => s.name == employmentStatus);
    onInputChange({
      target: { name: StepField.EMPLOYMENT_STATUS, value: status.id ?? '' },
    });

    onInputChange({
      target: { name: StepField.SECTOR_NAME, value: '' },
    });

    onInputChange({
      target: { name: StepField.JOB_TITLE, value: '' },
    });

    setJobTitles([]);
    setSelectedSector('');
    setSelectedJobTitle('');
  };

  const handleJobSectorsChange = selectedJobSector => {
    const sector = jobSectors.find(s => s.name == selectedJobSector);
    onInputChange({
      target: { name: StepField.SECTOR_NAME, value: sector.id ?? '' },
    });

    onInputChange({
      target: { name: StepField.JOB_TITLE, value: '' },
    });
    setJobTitles(sector?.jobTitles ?? []);
    setSelectedSector(selectedJobSector);
    setSelectedJobTitle('');
  };

  const handleJobTitleChange = jobTitle => {
    const title = jobTitles.find(t => t.name == jobTitle);
    onInputChange({
      target: { name: StepField.JOB_TITLE, value: title.id ?? '' },
    });
    setSelectedJobTitle(jobTitle);
  };

  const handleJobAntiquityChange = jobAntiquity => {
    const antiquity = jobAntiquities.find(a => a.name == jobAntiquity);
    onInputChange({
      target: { name: StepField.JOB_ANTIQUITY, value: antiquity.id ?? '' },
    });
    setSelectedJobAntiquity(jobAntiquity);
  };

  return (
    <main className={style.main}>
      <div className={style['form-content']}>
        <div className={style['form-header']}>
          <p className={style['form-header-title']}>
            {t(`STEP {step_number}/{step_count}`, {
              '{step_number}': stepNumber,
              '{step_count}': stepCount,
            })}
          </p>
        </div>
        <div className={style['form-title']}>
          <Title text={t(`What's your current Job?`)} />
        </div>
        <div className={style['form-inputs']}>
          <div className={style['form-control']}>
            <SelectBox
              label={t('Employment status')}
              title={t('Employment status')}
              placeholder={t('Select')}
              name={StepField.EMPLOYMENT_STATUS}
              closeOnChange={true}
              onChange={value => {
                handleEmploymentStatusChange(value);
              }}
              options={employmentStatuses.map(item => item.name)}
              value={selectedStatus}
            />
          </div>
          <div className={style['form-control']}>
            <SelectBox
              label={t('Sector name')}
              title={t('Sector name')}
              placeholder={t('Select')}
              name={StepField.SECTOR_NAME}
              closeOnChange={true}
              onChange={value => {
                handleJobSectorsChange(value);
              }}
              disabled={selectedStatus === ''}
              options={jobSectors.map(item => item.name)}
              value={selectedSector}
            />
          </div>
          <div className={style['form-control']}>
            <SelectBox
              label={t('Job title')}
              title={t('Job title')}
              placeholder={t('Select')}
              name={StepField.JOB_TITLE}
              closeOnChange={true}
              onChange={value => {
                handleJobTitleChange(value);
              }}
              disabled={jobTitles.length == 0}
              options={jobTitles.map(item => item.name)}
              value={selectedJobTitle}
            />
          </div>
          <div className={style['form-control']}>
            <SelectBox
              label={t('Job tenure')}
              title={t('Job tenure')}
              placeholder={t('Select')}
              name={StepField.JOB_ANTIQUITY}
              closeOnChange={true}
              onChange={value => {
                handleJobAntiquityChange(value);
              }}
              disabled={jobAntiquities.length == 0}
              options={jobAntiquities.map(item => item.name)}
              value={selectedJobAntiquity}
            />
          </div>
        </div>
      </div>
      <div className="fixed-cta">
        <Button fullWidth onClick={onButtonClick} disabled={!isButtonEnabled}>
          {t('Continue')}
        </Button>
      </div>
    </main>
  );
}
CurrentJobStep.Type = 'CurrentJobStep';
CurrentJobStep.EventName = 'current_job_step';
